/**
 * Email API service for ATS.
 */

import { createApiClient } from '@aaas/shared';
import type { BulkResultData, BulkResultResponse } from '../types/bulk';
import type {
  EmailTemplateCreateInput,
  EmailTemplateUpdateInput,
  EmailTemplateListResponse,
  EmailTemplateResponse,
  EmailThreadListResponse,
  EmailThreadResponse,
  SendEmailInput,
  SendEmailResponse,
  BulkSendEmailInput,
  BulkSendEmailResponse,
  PreviewEmailInput,
  PreviewEmailResponse,
  ReplyEmailInput,
  ReplyEmailResponse,
} from '../types';

export interface BulkEmailTemplateInput {
  name: string;
  category?: string | null;
  subject: string;
  body_html: string;
  body_text?: string | null;
}

interface ListTemplatesOptions {
  skip?: number;
  limit?: number;
  search?: string;
  category?: string;
}

interface ListThreadsOptions {
  skip?: number;
  limit?: number;
  cv_id?: string;
  job_id?: string;
  search?: string;
  direction?: 'sent' | 'received';
}

class EmailService {
  // ── Template CRUD ──

  async listTemplates(
    workspaceId: string,
    idToken: string,
    options: ListTemplatesOptions = {}
  ): Promise<EmailTemplateListResponse> {
    const { skip = 0, limit = 50, search, category } = options;
    const client = createApiClient(idToken, 'ats');
    const response = await client.get<EmailTemplateListResponse>('/emails/templates', {
      params: { skip, limit, search: search || undefined, category: category || undefined },
      headers: { 'X-Workspace-ID': workspaceId },
    });
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to list email templates');
    }
    return response.data;
  }

  async getTemplate(
    templateId: string,
    workspaceId: string,
    idToken: string
  ): Promise<EmailTemplateResponse> {
    const client = createApiClient(idToken, 'ats');
    const response = await client.get<EmailTemplateResponse>(
      `/emails/templates/${templateId}`,
      { headers: { 'X-Workspace-ID': workspaceId } }
    );
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to get email template');
    }
    return response.data;
  }

  async createTemplate(
    workspaceId: string,
    idToken: string,
    data: EmailTemplateCreateInput
  ): Promise<EmailTemplateResponse> {
    const client = createApiClient(idToken, 'ats');
    const response = await client.post<EmailTemplateResponse>('/emails/templates', data, {
      headers: { 'X-Workspace-ID': workspaceId },
    });
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to create email template');
    }
    return response.data;
  }

  /**
   * Bulk create email templates; duplicates skipped silently.
   */
  async bulkCreateTemplates(
    workspaceId: string,
    idToken: string,
    templates: readonly BulkEmailTemplateInput[]
  ): Promise<BulkResultData> {
    const client = createApiClient(idToken, 'ats');
    const response = await client.post<BulkResultResponse>(
      '/emails/templates/bulk',
      { templates },
      { headers: { 'X-Workspace-ID': workspaceId } }
    );
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to bulk create email templates');
    }
    return response.data.data;
  }

  async updateTemplate(
    templateId: string,
    workspaceId: string,
    idToken: string,
    data: EmailTemplateUpdateInput
  ): Promise<EmailTemplateResponse> {
    const client = createApiClient(idToken, 'ats');
    const response = await client.patch<EmailTemplateResponse>(
      `/emails/templates/${templateId}`,
      data,
      { headers: { 'X-Workspace-ID': workspaceId } }
    );
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to update email template');
    }
    return response.data;
  }

  async deleteTemplate(
    templateId: string,
    workspaceId: string,
    idToken: string
  ): Promise<void> {
    const client = createApiClient(idToken, 'ats');
    await client.delete(`/emails/templates/${templateId}`, {
      headers: { 'X-Workspace-ID': workspaceId },
    });
  }

  // ── Sending ──

  async sendEmail(
    workspaceId: string,
    idToken: string,
    data: SendEmailInput
  ): Promise<SendEmailResponse> {
    const client = createApiClient(idToken, 'ats');
    const response = await client.post<SendEmailResponse>('/emails/send', data, {
      headers: { 'X-Workspace-ID': workspaceId },
    });
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to send email');
    }
    return response.data;
  }

  async bulkSendEmail(
    workspaceId: string,
    idToken: string,
    data: BulkSendEmailInput
  ): Promise<BulkSendEmailResponse> {
    const client = createApiClient(idToken, 'ats');
    const response = await client.post<BulkSendEmailResponse>('/emails/bulk-send', data, {
      headers: { 'X-Workspace-ID': workspaceId },
    });
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to bulk send emails');
    }
    return response.data;
  }

  async previewEmail(
    workspaceId: string,
    idToken: string,
    data: PreviewEmailInput
  ): Promise<PreviewEmailResponse> {
    const client = createApiClient(idToken, 'ats');
    const response = await client.post<PreviewEmailResponse>('/emails/preview', data, {
      headers: { 'X-Workspace-ID': workspaceId },
    });
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to preview email');
    }
    return response.data;
  }

  // ── Reply ──

  async replyToThread(
    threadId: string,
    workspaceId: string,
    idToken: string,
    data: Omit<ReplyEmailInput, 'thread_id'>
  ): Promise<ReplyEmailResponse> {
    const client = createApiClient(idToken, 'ats');
    const response = await client.post<ReplyEmailResponse>(
      `/emails/threads/${threadId}/reply`,
      data,
      { headers: { 'X-Workspace-ID': workspaceId } }
    );
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to send reply');
    }
    return response.data;
  }

  // ── Threads / Messages ──

  async listThreads(
    workspaceId: string,
    idToken: string,
    options: ListThreadsOptions = {}
  ): Promise<EmailThreadListResponse> {
    const { skip = 0, limit = 50, cv_id, job_id, search, direction } = options;
    const client = createApiClient(idToken, 'ats');
    const response = await client.get<EmailThreadListResponse>('/emails/threads', {
      params: {
        skip,
        limit,
        cv_id: cv_id || undefined,
        job_id: job_id || undefined,
        search: search || undefined,
        direction: direction || undefined,
      },
      headers: { 'X-Workspace-ID': workspaceId },
    });
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to list email threads');
    }
    return response.data;
  }

  async getThread(
    threadId: string,
    workspaceId: string,
    idToken: string
  ): Promise<EmailThreadResponse> {
    const client = createApiClient(idToken, 'ats');
    const response = await client.get<EmailThreadResponse>(
      `/emails/threads/${threadId}`,
      { headers: { 'X-Workspace-ID': workspaceId } }
    );
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to get email thread');
    }
    return response.data;
  }
}

export default new EmailService();
